/**
 * One activity timeline from what the page already reads (round 2, support R2-M2; the step 4 feed
 * pulled forward): the audit stream (customer and staff actions, support grants), the product events
 * (sign-ups, first pages, exports, billing, with a cancellation's reason), staff notes, credit packs and
 * staff credit changes (refills and use are the ledger's and the charts', not activity), and the
 * batches the company sent with how they settled (round 3, support B4), newest first, each with who
 * did it and a link where there is one. Pure.
 */
import type { AccountNote, AuditEvent, LedgerEntry, RecentEvent } from '../types';
import type { AccountBatch } from './account-types';
import { intakeWord } from './BreakdownPanel';
import { eventWords } from '../analytics/journey-words';

export type ActivityWho = 'customer' | 'staff' | 'paige';

export interface ActivityItem {
  key: string;
  at: string;
  who: ActivityWho;
  actor: string;
  text: string;
  detail: string | null;
  /** The document timeline for a batch's first document. */
  href?: string;
}

export interface ActivityInput {
  /** Null: the caller's staff role does not read the company's own activity. */
  audit: readonly AuditEvent[] | null;
  events: readonly RecentEvent[];
  notes: readonly AccountNote[];
  ledger: readonly LedgerEntry[];
  batches?: readonly AccountBatch[] | undefined;
}

const STAFF = /@ademero\.com$|^staff$|support/i;

function fromAudit(e: AuditEvent): ActivityItem {
  const staff = STAFF.test(e.actor) || e.action.startsWith('staff.');
  return {
    key: `a-${e.id}`,
    at: e.at,
    who: staff ? 'staff' : 'customer',
    actor: e.actor,
    text: e.action.replace(/[._]/g, ' '),
    detail: e.detail || null,
  };
}

function fromEvent(e: RecentEvent, i: number): ActivityItem {
  return {
    key: `e-${e.at}-${i}`,
    at: e.at,
    who: e.source === 'server' ? 'paige' : 'customer',
    actor: e.source === 'server' ? 'Paige' : 'someone at the company',
    text: eventWords(e),
    detail: null,
  };
}

function fromNote(n: AccountNote): ActivityItem {
  const text =
    n.kind === 'next_step'
      ? `next step${n.dueOn ? `, due ${n.dueOn}` : ''}${n.doneAt ? ' (done)' : ''}`
      : 'note';
  return { key: `n-${n.id}`, at: n.createdAt, who: 'staff', actor: n.author, text, detail: n.body };
}

function fromLedger(r: LedgerEntry): ActivityItem {
  const sign = r.delta < 0 ? '−' : '+';
  const who: ActivityWho =
    r.actor === 'staff' || STAFF.test(r.actor ?? '')
      ? 'staff'
      : r.actor === 'card'
        ? 'customer'
        : 'paige';
  return {
    key: `l-${r.id}`,
    at: r.at,
    who,
    actor: r.actor ?? 'Paige',
    text: `pages ${sign}${Math.abs(Math.round(r.delta)).toLocaleString('en-US')}`,
    detail: r.reason,
  };
}

/** "Sent 12 pages by email to AP invoices"; then how it settled. */
function fromBatch(b: AccountBatch): ActivityItem {
  const how = intakeWord[b.source as keyof typeof intakeWord] ?? b.source;
  const settled = [
    b.exported ? `${b.exported} exported` : null,
    b.inReview ? `${b.inReview} in review` : null,
    b.exceptions ? `${b.exceptions} exceptions` : null,
  ].filter(Boolean);
  const state = b.completedAt
    ? `finished: ${settled.join(', ') || 'nothing left to do'}`
    : `${b.state.replace('_', ' ')}${settled.length ? `: ${settled.join(', ')}` : ''}`;
  return {
    key: `b-${b.id}`,
    at: b.receivedAt,
    who: 'customer',
    actor: how,
    text: `sent ${b.pages} page${b.pages === 1 ? '' : 's'} to ${b.job}`,
    detail: state,
    ...(b.firstDocument
      ? { href: `/admin/timeline?q=${encodeURIComponent(b.firstDocument)}` }
      : {}),
  };
}

/** Rows that are activity: packs and staff changes; refills and use stay in the ledger and the charts. */
const isActivity = (r: LedgerEntry) =>
  r.kind === 'pack' || r.kind === 'staff' || r.kind === 'refund' || r.kind === 'adjustment';

/** Everything, newest first. */
export function activityOf(i: ActivityInput): ActivityItem[] {
  return [
    ...(i.audit ?? []).map(fromAudit),
    ...i.events.map(fromEvent),
    ...i.notes.map(fromNote),
    ...(i.batches ?? []).map(fromBatch),
    ...i.ledger.filter(isActivity).map(fromLedger),
  ].sort((a, b) => b.at.localeCompare(a.at));
}
